/**
 * Shell telemetry sink — a single surface every shell-level diagnostic routes
 * through (error boundaries, global error/rejection handlers, federated
 * remote-load failures, web-vitals, route timings).
 *
 * Design:
 *   - In development it forwards to the console with a `[tyndo:telemetry]`
 *     prefix so events stay greppable in devtools.
 *   - In production it batches events and POSTs them to the gateway RUM
 *     endpoint (`/api/v1/ui-telemetry/events`) on a short timer / on
 *     `visibilitychange`, using `navigator.sendBeacon` when the page is
 *     unloading so nothing is lost on navigation away. Errors always surface
 *     to the console too so an operator can copy a stack trace.
 *   - Every event carries a stable per-session correlation id so a UI error
 *     can be joined to gateway traces (the same id is propagated as the
 *     `x-correlation-id` request header by the API client, see
 *     `correlation.ts`).
 *
 * This is intentionally dependency-free (no OTel/web-vitals npm package) so it
 * cannot break the shell boot; the gateway endpoint is best-effort and a
 * failure to deliver telemetry never throws into product code.
 */

import { getCorrelationId } from './correlation.ts';

export type TelemetryLevel = 'error' | 'warn' | 'info';

export interface TelemetryEvent {
  /** Event name, e.g. 'shell.error', 'federation.load_failed', 'web_vital'. */
  name: string;
  level: TelemetryLevel;
  /** Free-form structured attributes. */
  attributes?: Record<string, unknown>;
  /** Epoch millis the event was recorded. */
  timestamp: number;
  /** Per-session correlation id, joins UI events to gateway traces. */
  correlationId: string;
}

const isProduction = (() => {
  try {
    return typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
  } catch {
    return false;
  }
})();

const TELEMETRY_ENDPOINT = '/api/v1/ui-telemetry/events';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER = 50;

let buffer: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function resolveEndpoint(): string {
  const g =
    typeof globalThis !== 'undefined'
      ? (globalThis as typeof globalThis & Record<string, unknown>)
      : undefined;
  const base = g?.TYNDO_API_BASE_URL ? String(g.TYNDO_API_BASE_URL) : '';
  return base ? `${base}${TELEMETRY_ENDPOINT}` : TELEMETRY_ENDPOINT;
}

function flush(useBeacon = false): void {
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  const payload = JSON.stringify({ events: batch });
  const url = resolveEndpoint();

  try {
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
    // audit-allow: direct-fetch -- observability transport: best-effort
    // telemetry delivery uses sendBeacon/keepalive and must fire during page
    // unload, so it cannot route through the React-hook shared API client
    // (which is unavailable outside the component tree / on teardown).
    void fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-correlation-id': getCorrelationId(),
      },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* best-effort — telemetry delivery must never throw into product code. */
    });
  } catch {
    /* swallow — never let telemetry break the app. */
  }
}

function ensureFlushTimer(): void {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

/**
 * Record a structured telemetry event. Errors and warnings always echo to the
 * console; in production every event is also queued for delivery to the RUM
 * endpoint.
 */
export function recordEvent(
  name: string,
  level: TelemetryLevel,
  attributes?: Record<string, unknown>
): void {
  const event: TelemetryEvent = {
    name,
    level,
    attributes,
    timestamp: Date.now(),
    correlationId: getCorrelationId(),
  };

  // Console echo — errors/warns always, info only in dev.
  if (level === 'error') {
    console.error(`[tyndo:telemetry] ${name}`, attributes ?? '');
  } else if (level === 'warn') {
    console.warn(`[tyndo:telemetry] ${name}`, attributes ?? '');
  } else if (!isProduction) {
    console.info(`[tyndo:telemetry] ${name}`, attributes ?? '');
  }

  if (!isProduction) return;

  buffer.push(event);
  if (buffer.length >= MAX_BUFFER) {
    flush(false);
  } else {
    ensureFlushTimer();
  }
}

/** Convenience wrapper for error-level events with an Error object. */
export function recordError(
  name: string,
  error: unknown,
  attributes?: Record<string, unknown>
): void {
  const err = error instanceof Error ? error : new Error(String(error));
  recordEvent(name, 'error', {
    ...attributes,
    message: err.message,
    stack: err.stack,
  });
}

/**
 * Register window-level `error` / `unhandledrejection` listeners so a throw
 * anywhere outside a React render (event handlers, async callbacks, timers,
 * promise chains) is captured rather than vanishing into the console. Idempotent.
 */
let globalHandlersInstalled = false;
export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    recordError('window.error', event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    recordError('window.unhandledrejection', event.reason, {});
  });
}
